/**
 * Inventory Schema Utility Functions
 * Implements Template → Scheduled → History model for product inventory management
 */

/**
 * Automatic migration of expired scheduled inventory into history
 * @param {Object} inventory - Inventory object with scheduled and history arrays
 * @returns {Object} Updated inventory with expired rows moved to history
 */
export function migrateExpiredInventory(inventory) {
  const startTime = performance.now();
  
  if (!inventory?.scheduled || !Array.isArray(inventory.scheduled)) {
    console.log('🔍 No scheduled inventory to migrate');
    return inventory;
  }

  const today = new Date();
  const todayString = today.toISOString().split('T')[0];
  
  console.log(`🔍 Starting inventory migration for dates before ${todayString}`);
  console.log(`🔍 Today's date object:`, today);
  console.log(`🔍 Today's ISO string:`, today.toISOString());
  console.log(`🔍 Current scheduled rows: ${inventory.scheduled.length}`);
  
  // Initialize history array if it doesn't exist
  if (!inventory.history) {
    inventory.history = [];
  }
  
  const expiredRows = [];
  const validRows = [];
  
  // Separate expired and valid rows
  inventory.scheduled.forEach(row => {
    // Parse the service date and compare as Date objects for accurate comparison
    const serviceDate = new Date(row.service_date);
    serviceDate.setHours(0, 0, 0, 0); // Set to start of day for accurate comparison
    
    console.log(`🔍 Checking row ${row.service_date}: parsed=${serviceDate.toISOString().split('T')[0]}, today=${today.toISOString().split('T')[0]}, isExpired=${serviceDate < today}`);
    
    // More explicit date comparison to avoid timezone issues
    const serviceDateOnly = serviceDate.toISOString().split('T')[0];
    const todayOnly = today.toISOString().split('T')[0];
    
    if (serviceDateOnly < todayOnly) {
      console.log(`🔍 Row ${row.service_date} is EXPIRED (${serviceDateOnly} < ${todayOnly})`);
      expiredRows.push(row);
    } else {
      console.log(`🔍 Row ${row.service_date} is VALID (${serviceDateOnly} >= ${todayOnly})`);
      validRows.push(row);
    }
  });
  
  if (expiredRows.length === 0) {
    const endTime = performance.now();
    console.log(`🔍 No expired rows to migrate (${(endTime - startTime).toFixed(2)}ms)`);
    return inventory;
  }
  
  console.log(`🔍 Found ${expiredRows.length} expired rows to migrate`);
  
  // Migrate expired rows to history
  expiredRows.forEach(expiredRow => {
    // Check if this row is already in history (idempotency)
    const alreadyMigrated = inventory.history.some(historyRow => 
      historyRow.service_date === expiredRow.service_date
    );
    
    if (alreadyMigrated) {
      console.log(`🔍 Row for ${expiredRow.service_date} already migrated, skipping`);
      return;
    }
    
    // Create history entry with required fields
    const historyEntry = {
      service_date: expiredRow.service_date,
      source: expiredRow.source || 'template',
      enabled: expiredRow.enabled || false,
      max_total_planned: expiredRow.max_total || 0,
      ordered_total: expiredRow.ordered_total || 0,
      available_end_of_day: expiredRow.available_total || 0,
      migrated_at: new Date().toISOString(),
      // Copy windows if they exist
      ...(expiredRow.windows && {
        windows: expiredRow.windows.map(window => ({
          start_time_local: window.start_time_local,
          end_time_local: window.end_time_local,
          max_per_window_planned: window.max_per_window || null,
          ordered_qty: window.ordered_qty || 0,
          available_end_of_window: window.available_qty || 0
        }))
      })
    };
    
    inventory.history.push(historyEntry);
    console.log(`✅ Migrated row for ${expiredRow.service_date} to history`);
  });
  
  // Update scheduled array to only contain valid (future/current) rows
  inventory.scheduled = validRows;
  
  const endTime = performance.now();
  const duration = endTime - startTime;
  
  console.log(`✅ Migration complete: ${expiredRows.length} rows moved to history, ${validRows.length} rows remain in scheduled (${duration.toFixed(2)}ms)`);
  
  // Performance warning if migration takes too long
  if (duration > 200) {
    console.warn(`⚠️ Migration took ${duration.toFixed(2)}ms (>200ms threshold) for ${inventory.scheduled.length} rows`);
  }
  
  return inventory;
}

/**
 * Auto-generate weekly scheduled inventory from templates
 * Ensures products always have upcoming scheduled rows without cron jobs
 * @param {Object} inventory - Inventory object with policy, template, and scheduled arrays
 * @returns {Object} Updated inventory with missing scheduled rows generated
 */
export function autoGenerateScheduledInventory(inventory) {
  const startTime = performance.now();
  
  if (!inventory?.template || !Array.isArray(inventory.template)) {
    console.log('🔍 No template found for auto-generation');
    return inventory;
  }
  
  if (!inventory.policy?.start_date) {
    console.log('🔍 No policy start_date found for auto-generation');
    return inventory;
  }
  
  // Initialize scheduled array if it doesn't exist
  if (!inventory.scheduled) {
    inventory.scheduled = [];
  }
  
  const today = new Date();
  const todayString = today.toISOString().split('T')[0];
  
  // Calculate horizon based on order_ahead_days or default to 30 days to ensure we generate enough dates
  const orderAheadDays = inventory.policy?.order_ahead_days || 30;
  const horizonDays = Math.max(30, orderAheadDays);
  
  // Check if we should start generating from policy start_date or today
  let startDate = new Date(today);
  const policyStartDate = new Date(inventory.policy.start_date);
  
  if (policyStartDate > today) {
    startDate = policyStartDate;
    console.log(`🔍 Policy start_date ${inventory.policy.start_date} is in the future, starting generation from there`);
  } else {
    // Start from tomorrow if we're starting from today, to avoid generating today's date
    startDate.setDate(today.getDate() + 1);
    console.log(`🔍 Starting generation from tomorrow: ${startDate.toISOString().split('T')[0]}`);
  }
  
  // Check if we have an end_date constraint
  const endDate = inventory.policy?.end_date ? new Date(inventory.policy.end_date) : null;
  
  console.log(`🔍 Auto-generating scheduled inventory: horizon = ${horizonDays} days from ${todayString}`);
  console.log(`🔍 Current scheduled rows: ${inventory.scheduled.length}`);
  console.log(`🔍 Start date for generation: ${startDate.toISOString().split('T')[0]}`);
  console.log(`🔍 End date constraint: ${endDate ? endDate.toISOString().split('T')[0] : 'none'}`);
  console.log(`🔍 Expected end date for generation: ${new Date(startDate.getTime() + (horizonDays - 1) * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}`);
  
  if (endDate) {
    console.log(`🔍 Policy end_date constraint: ${inventory.policy.end_date}`);
  }
  
  const generatedRows = [];
  const existingDates = new Set(inventory.scheduled.map(row => row.service_date));
  
  // Generate rows for each day in the horizon
  for (let i = 0; i < horizonDays; i++) {
    const serviceDate = new Date(startDate);
    serviceDate.setDate(startDate.getDate() + i);
    const serviceDateString = serviceDate.toISOString().split('T')[0];
    
    // Skip if we've hit the end_date constraint
    if (endDate && serviceDate > endDate) {
      console.log(`🔍 Skipping ${serviceDateString}: past end_date ${inventory.policy.end_date}`);
      break;
    }
    
    // Skip if row already exists
    if (existingDates.has(serviceDateString)) {
      console.log(`🔍 Skipping ${serviceDateString}: row already exists`);
      continue;
    }
    
    // Get weekday template (0 = Sunday, 1 = Monday, etc.)
    const weekday = serviceDate.getDay();
    
    console.log(`🔍 Processing date ${serviceDateString} (weekday ${weekday})`);
    const template = inventory.template[weekday];
    
    if (!template) {
      console.log(`⚠️ No template found for weekday ${weekday} (${serviceDateString})`);
      continue;
    }
    
    // Generate scheduled row based on template
    const scheduledRow = {
      service_date: serviceDateString,
      source: 'template',
      enabled: template.enabled || false,
      max_total: template.enabled ? (template.default_max_total || 0) : 0,
      ordered_total: 0,
      available_total: template.enabled ? (template.default_max_total || 0) : 0,
      start_time_local: template.start_time_local || '08:00',
      end_time_local: template.end_time_local || '18:00'
    };
    
    // Add windows if they exist in template
    if (template.windows && Array.isArray(template.windows)) {
      scheduledRow.windows = template.windows.map(window => ({
        start_time_local: window.start_time_local || '08:00',
        end_time_local: window.end_time_local || '18:00',
        max_per_window: window.max_per_window || null,
        ordered_qty: 0,
        available_qty: window.max_per_window || 0,
        cutoff_minutes: window.cutoff_minutes || null
      }));
    }
    
    generatedRows.push(scheduledRow);
    existingDates.add(serviceDateString);
    
    console.log(`✅ Generated row for ${serviceDateString}: enabled=${scheduledRow.enabled}, max_total=${scheduledRow.max_total}, weekday=${weekday}`);
  }
  
  // Add generated rows to scheduled array
  if (generatedRows.length > 0) {
    inventory.scheduled.push(...generatedRows);
    console.log(`✅ Added ${generatedRows.length} new scheduled rows`);
  } else {
    console.log(`🔍 No new rows needed: all ${horizonDays} days already covered`);
  }
  
  const endTime = performance.now();
  const duration = endTime - startTime;
  
  console.log(`✅ Auto-generation complete: ${inventory.scheduled.length} total scheduled rows (${duration.toFixed(2)}ms)`);
  
  // Performance warning if generation takes too long
  if (duration > 200) {
    console.warn(`⚠️ Auto-generation took ${duration.toFixed(2)}ms (>200ms threshold) for ${horizonDays} days`);
  }
  
  return inventory;
}

/**
 * Generate weekday templates for a new product
 * @returns {Array} Array of 7 weekday template objects
 */
export function generateWeekdayTemplates() {
  const weekdays = [
    { weekday: 0, name: 'sunday' },
    { weekday: 1, name: 'monday' },
    { weekday: 2, name: 'tuesday' },
    { weekday: 3, name: 'wednesday' },
    { weekday: 4, name: 'thursday' },
    { weekday: 5, name: 'friday' },
    { weekday: 6, name: 'saturday' }
  ];

  return weekdays.map(({ weekday, name }) => ({
    weekday,
    enabled: false,
    default_max_total: 0,
    start_time_local: '08:00',
    end_time_local: '18:00'
  }));
}

/**
 * Generate scheduled availability rows for the next 28 days
 * @param {Object} policy - Inventory policy object
 * @param {Array} templates - Weekday templates
 * @returns {Array} Array of scheduled availability objects
 */
export function generateScheduledAvailability(policy, templates) {
  const scheduled = [];
  
  // Parse start_date in local timezone to avoid UTC conversion issues
  const [year, month, day] = policy.start_date.split('-').map(Number);
  const startDate = new Date(year, month - 1, day); // month is 0-indexed
  
  // Generate 28 days starting from start_date
  for (let i = 0; i < 28; i++) {
    const serviceDate = new Date(startDate);
    serviceDate.setDate(startDate.getDate() + i);
    
    const weekday = serviceDate.getDay();
    const template = templates.find(t => t.weekday === weekday);
    
    // Format date as YYYY-MM-DD in local timezone
    const serviceDateString = serviceDate.toLocaleDateString('en-CA'); // Returns YYYY-MM-DD format
    
    if (template && template.enabled) {
      // Inherit from template
      scheduled.push({
        service_date: serviceDateString,
        source: 'template',
        enabled: true,
        max_total: template.default_max_total,
        ordered_total: 0,
        available_total: template.default_max_total - 0, // ordered_total starts at 0
        start_time_local: template.start_time_local,
        end_time_local: template.end_time_local
      });
    } else {
      // Disabled or no template
      scheduled.push({
        service_date: serviceDateString,
        source: 'template',
        enabled: false,
        max_total: 0,
        ordered_total: 0,
        available_total: 0,
        start_time_local: '08:00',
        end_time_local: '18:00'
      });
    }
  }
  
  return scheduled;
}

/**
 * Generate default inventory policy
 * @returns {Object} Default inventory policy
 */
export function generateDefaultInventoryPolicy() {
  const today = new Date();
  
  // Find the most recent Sunday (weekday 0) to start the week
  const startDate = new Date(today);
  const currentDay = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
  
  // Calculate days back to last Sunday
  // If today is Sunday (0), we want 0 days back
  // If today is Monday (1), we want 1 day back 
  // If today is Saturday (6), we want 6 days back
  const daysBackToSunday = currentDay === 0 ? 0 : currentDay;
  startDate.setDate(today.getDate() - daysBackToSunday);
  
  const endDate = new Date(startDate);
  endDate.setDate(startDate.getDate() + 90); // 90 days from start of week
  
  return {
    start_date: startDate.toISOString().split('T')[0],
    end_date: endDate.toISOString().split('T')[0],
    order_ahead_days: 7,
    same_day_allowed: true,
    cutoff_minutes: 120, // 2 hours before service
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
  };
}

/**
 * Create complete inventory structure for a new product
 * @returns {Object} Complete inventory object with templates, scheduled, and history
 */
export function createNewProductInventory() {
  const policy = generateDefaultInventoryPolicy();
  const templates = generateWeekdayTemplates();
  const scheduled = generateScheduledAvailability(policy, templates);
  
  return {
    policy,
    template: templates,
    scheduled,
    history: [] // Empty for new products
  };
}

/**
 * Update scheduled rows when template changes
 * @param {Object} inventory - Current inventory object
 * @param {number} weekday - Weekday to update (0-6)
 * @param {Object} templateUpdates - Template field updates
 * @returns {Object} Updated inventory object
 */
export function updateTemplateAndPropagate(inventory, weekday, templateUpdates) {
  const updatedInventory = { ...inventory };
  
  // Update the template
  const templateIndex = updatedInventory.template.findIndex(t => t.weekday === weekday);
  if (templateIndex !== -1) {
    updatedInventory.template[templateIndex] = {
      ...updatedInventory.template[templateIndex],
      ...templateUpdates
    };
  }
  
  // Propagate changes to future scheduled rows with source="template"
  const today = new Date();
  updatedInventory.scheduled = updatedInventory.scheduled.map(row => {
    const rowDate = new Date(row.service_date);
    const isFuture = rowDate >= today;
    const isTemplateSource = row.source === 'template';
    const isSameWeekday = rowDate.getDay() === weekday;
    
    if (isFuture && isTemplateSource && isSameWeekday) {
      // Apply template changes to future template-sourced rows
      return {
        ...row,
        enabled: templateUpdates.enabled !== undefined ? templateUpdates.enabled : row.enabled,
        max_total: templateUpdates.default_max_total !== undefined ? templateUpdates.default_max_total : row.max_total,
        start_time_local: templateUpdates.start_time_local !== undefined ? templateUpdates.start_time_local : row.start_time_local,
        end_time_local: templateUpdates.end_time_local !== undefined ? templateUpdates.end_time_local : row.end_time_local,
        available_total: templateUpdates.default_max_total !== undefined ? 
          Math.max(0, templateUpdates.default_max_total - row.ordered_total) : row.available_total
      };
    }
    
    return row;
  });
  
  return updatedInventory;
}

/**
 * Update a scheduled row (flips source to "manual")
 * @param {Object} inventory - Current inventory object
 * @param {string} serviceDate - Service date (YYYY-MM-DD)
 * @param {Object} rowUpdates - Row field updates
 * @returns {Object} Updated inventory object
 */
export function updateScheduledRow(inventory, serviceDate, rowUpdates) {
  const updatedInventory = { ...inventory };
  
  const rowIndex = updatedInventory.scheduled.findIndex(row => row.service_date === serviceDate);
  if (rowIndex !== -1) {
    const currentRow = updatedInventory.scheduled[rowIndex];
    
    // Validate max_total cannot be lower than existing orders
    if (rowUpdates.max_total !== undefined && rowUpdates.max_total < currentRow.ordered_total) {
      throw new Error(`Cannot set max_total lower than existing orders (${currentRow.ordered_total})`);
    }
    
    // Update the row and flip source to manual
    updatedInventory.scheduled[rowIndex] = {
      ...currentRow,
      ...rowUpdates,
      source: 'manual',
      available_total: rowUpdates.max_total !== undefined ? 
        Math.max(0, rowUpdates.max_total - currentRow.ordered_total) : currentRow.available_total
    };
  }
  
  return updatedInventory;
}

/**
 * Move past scheduled rows to history
 * @param {Object} inventory - Current inventory object
 * @returns {Object} Updated inventory object with history populated
 */
export function movePastRowsToHistory(inventory) {
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Start of day
  
  const updatedInventory = { ...inventory };
  const currentScheduled = [];
  const newHistory = [...updatedInventory.history];
  
  updatedInventory.scheduled.forEach(row => {
    const rowDate = new Date(row.service_date);
    rowDate.setHours(0, 0, 0, 0);
    
    if (rowDate < today) {
      // Move to history
      newHistory.push({
        service_date: row.service_date,
        enabled: row.enabled,
        max_total_planned: row.max_total,
        ordered_total: row.ordered_total,
        start_time_local: row.start_time_local,
        end_time_local: row.end_time_local
      });
    } else {
      // Keep in scheduled
      currentScheduled.push(row);
    }
  });
  
  updatedInventory.scheduled = currentScheduled;
  updatedInventory.history = newHistory;
  
  return updatedInventory;
}

/**
 * Get available inventory for a specific date
 * @param {Object} inventory - Product inventory object
 * @param {string} serviceDate - Service date (YYYY-MM-DD)
 * @returns {Object|null} Available inventory info or null if not available
 */
export function getAvailableInventoryForDate(inventory, serviceDate) {
  const scheduledRow = inventory.scheduled.find(row => row.service_date === serviceDate);
  
  if (!scheduledRow || !scheduledRow.enabled) {
    return null;
  }
  
  return {
    max_total: scheduledRow.max_total,
    ordered_total: scheduledRow.ordered_total,
    available_total: scheduledRow.available_total,
    start_time_local: scheduledRow.start_time_local,
    end_time_local: scheduledRow.end_time_local
  };
}

/**
 * Check if product has any available inventory
 * @param {Object} inventory - Product inventory object
 * @returns {boolean} True if product has available inventory
 */
export function hasAvailableInventory(inventory) {
  if (!inventory || !inventory.scheduled) {
    return false;
  }
  
  return inventory.scheduled.some(row => 
    row.enabled && row.available_total > 0
  );
}

/**
 * Ensure product has complete inventory schema with auto-generated scheduled rows
 * Creates missing inventory schema if needed and generates scheduled rows
 * @param {Object} product - Product object that may or may not have inventory
 * @returns {Object} Product with complete inventory schema
 */
export function ensureCompleteInventorySchema(product) {
  if (!product) return product;
  
  // If no inventory schema exists, create a complete one
  if (!product.inventory) {
    console.log(`🔧 Creating complete inventory schema for ${product.name}`);
    product.inventory = createNewProductInventory();
  }
  
  // Ensure we have templates
  if (!product.inventory.template || !Array.isArray(product.inventory.template)) {
    console.log(`🔧 Creating weekday templates for ${product.name}`);
    product.inventory.template = generateWeekdayTemplates();
  }
  
  // Ensure we have policy
  if (!product.inventory.policy) {
    console.log(`🔧 Creating inventory policy for ${product.name}`);
    product.inventory.policy = generateDefaultInventoryPolicy();
  }
  
  // Auto-generate scheduled rows
  const completeInventory = autoGenerateScheduledInventory(product.inventory);
  product.inventory = completeInventory;
  
  return product;
}
